import React, { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  setPage,
  setLimit,
  setSortBy,
  setDefaultSort,
  setBalances,
  setTotal,
} from "../../store/slices/holidayBalancesSlice.ts";
import { RootState } from "../../store/index.ts";
import { fixedHeadStyle, fixedStyle, getAntiguedad } from "../../utils/constanst.js";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import moment from "moment";
import { Tooltip } from "@mui/material";
import { 
  getPartnerBalanceDetails, 
  updateHolidayBalance, 
  deleteHolidayBalance,
  getAllBalancesPaginated
} from "../../services/holidays.service.ts";
import { notification } from "../../utils/Notifications.tsx";
import ModalContainer from "../ModalContainer.tsx";
import HolidayBalanceForm from "./HolidayBalanceForm.tsx";

interface GroupedBalance {
  partnerId: number;
  partnerName: string;
  totalDays: number;
  totalDaysTaken: number;
  totalDaysPending: number;
  totalDaysRemaining: number;
  periodsCount: number;
  periods?: any[];
  lastUpdated: string;
  partner?: any;
}

interface PartnerDetails {
  partnerId: number;
  partnerName: string;
  totalDays: number;
  totalDaysTaken: number;
  totalDaysPending: number;
  totalDaysRemaining: number;
  periodsCount: number;
  periods: any[];
  lastUpdated: string;
  partner: any;
}

interface HolidayBalancesGroupedTableProps {
  onRefresh?: () => void;
}

const HolidayBalancesGroupedTable = ({ onRefresh }: HolidayBalancesGroupedTableProps = { onRefresh: undefined }) => {
  const dispatch = useDispatch();
  const { total, page, limit, balances, sortBy, defaultSort, query } = useSelector(
    (state: RootState) => state.holidayBalances
  );
  
  // Obtener bossId desde los parámetros de la URL si existe
  const { bossId } = useParams<{ bossId?: string }>();

  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [partnerDetails, setPartnerDetails] = useState<
    Map<number, PartnerDetails>
  >(new Map());
  const [loadingDetails, setLoadingDetails] = useState<Set<number>>(new Set());
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingBalance, setEditingBalance] = useState<any>(null);
  const [deletingBalance, setDeletingBalance] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const handlePageChange = (newPage: number) => {
    dispatch(setPage(newPage));
  };

  const handleLimitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newLimit = parseInt(event.target.value);
    dispatch(setLimit(newLimit));
    dispatch(setPage(0));
  };

  const setSort = (sort: string) => {
    const sortOrder =
      sortBy !== sort ? "asc" : defaultSort === "asc" ? "desc" : "asc";
    dispatch(setSortBy(sort));
    dispatch(setPage(0));
    dispatch(setDefaultSort(sortOrder));
  };

  const getIconSort = () => {
    return (
      <i
        className={`fa-solid fa-sort-${defaultSort === "asc" ? "up" : "down"}`}
      />
    );
  };

  const formatPartnerName = (partner: any) => {
    const fullName = [
      partner.person?.firstName || "",
      partner.person?.lastName || "",
      partner.person?.secondLastName || "",
    ]
      .join(" ")
      .replace(/\s+/g, " ")
      .trim();

    return toCamelCase(fullName);
  };

  const formatPeriod = (holidayPeriod: any) => {
    return `${holidayPeriod.startYear}-${holidayPeriod.endYear}`;
  };

  const groupedBalances = useMemo((): GroupedBalance[] => {
    if (!balances || balances.length === 0) return [];

    const transformedBalances = balances.map((balance: any) => ({
      partnerId: balance.partnerid || balance.partnerId,
      partnerName: balance.partnername || balance.partnerName || "",
      totalDays: parseInt(balance.totaldays || balance.totalDays || "0"),
      totalDaysTaken: parseInt(
        balance.totaldaystaken || balance.totalDaysTaken || "0"
      ),
      totalDaysPending: parseInt(
        balance.totaldayspending || balance.totalDaysPending || "0"
      ),
      totalDaysRemaining: parseInt(
        balance.totaldaysremaining || balance.totalDaysRemaining || "0"
      ),
      periodsCount: parseInt(
        balance.periodscount || balance.periodsCount || "0"
      ),
      lastUpdated:
        balance.lastupdated || balance.lastUpdated || new Date().toISOString(),
      periods: balance.periods || [],
      partner: balance.partner || null,
      position:
        balance.positionname || balance.positionName ||
        balance.position ||
        balance.partner?.candidate?.vacancy?.position?.name || null,
      recruitmentDate:
        balance.recruitmentdate || balance.recruitmentDate ||
        balance.partner?.candidate?.recruitmentDate || null,
    }));

    return [...transformedBalances].sort(
      (a: GroupedBalance, b: GroupedBalance) => {
        switch (sortBy) {
          case "partnerName":
            return defaultSort === "asc"
              ? a.partnerName.localeCompare(b.partnerName)
              : b.partnerName.localeCompare(a.partnerName);
          case "totalDays":
            return defaultSort === "asc"
              ? a.totalDays - b.totalDays
              : b.totalDays - a.totalDays;
          case "totalDaysTaken":
            return defaultSort === "asc"
              ? a.totalDaysTaken - b.totalDaysTaken
              : b.totalDaysTaken - a.totalDaysTaken;
          case "totalDaysPending":
            return defaultSort === "asc"
              ? a.totalDaysPending - b.totalDaysPending
              : b.totalDaysPending - a.totalDaysPending;
          case "totalDaysRemaining":
            return defaultSort === "asc"
              ? a.totalDaysRemaining - b.totalDaysRemaining
              : b.totalDaysRemaining - a.totalDaysRemaining;
          case "periodsCount":
            return defaultSort === "asc"
              ? a.periodsCount - b.periodsCount
              : b.periodsCount - a.periodsCount;
          default:
            return a.partnerName.localeCompare(b.partnerName);
        }
      }
    );
  }, [balances, sortBy, defaultSort]);

  const toggleRowExpansion = async (partnerId: number) => {
    const newExpanded = new Set(expandedRows);

    if (newExpanded.has(partnerId)) {
      newExpanded.delete(partnerId);
    } else {
      newExpanded.add(partnerId);
      await loadPartnerDetails(partnerId);
    }
    setExpandedRows(newExpanded);
  };

  const loadPartnerDetails = async (partnerId: number) => {
    if (loadingDetails.has(partnerId)) return;

    setLoadingDetails((prev) => new Set(prev).add(partnerId));

    try {
      const details = await getPartnerBalanceDetails(partnerId);

      setPartnerDetails((prev) => new Map(prev).set(partnerId, details));
    } catch (error) {
      console.error("Error loading partner details:", error);
      notification(
        "Error",
        "Error al cargar los detalles del colaborador",
        "danger"
      );
    } finally {
      setLoadingDetails((prev) => {
        const newSet = new Set(prev);
        newSet.delete(partnerId);
        return newSet;
      });
    }
  };

  const handleEdit = (balance: any) => {
    setEditingBalance(balance);
    setShowEditModal(true);
  };

  const handleDelete = (balance: any) => {
    setDeletingBalance(balance);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingBalance) return;

    // Buscar el partnerId desde los detalles ya cargados
    let partnerId: number | null = null;
    for (const [pid, details] of partnerDetails.entries()) {
      if (details.periods?.some(p => p.id === deletingBalance.id)) {
        partnerId = pid;
        break;
      }
    }

    try {
      setLoading(true);
      await deleteHolidayBalance(deletingBalance.id);
      notification("Éxito", "Balance de vacaciones eliminado correctamente", "success");
      setShowDeleteModal(false);
      setDeletingBalance(null);
      
      // Recargar los balances agrupados para actualizar los totales
      await reloadBalances();
      
      // Recargar los detalles del partner si está expandido
      if (partnerId && expandedRows.has(partnerId)) {
        await loadPartnerDetails(partnerId);
      }
      
      // Llamar al callback de refresh si existe (desde el componente padre)
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error("Error deleting balance:", error);
      notification("Error", "No se pudo eliminar el balance de vacaciones", "danger");
    } finally {
      setLoading(false);
    }
  };

  const reloadBalances = async () => {
    try {
      const offset = page * limit;
      const branchId = query.includes("branchId=")
        ? query.split("branchId=")[1]?.split("&")[0] || "all"
        : "all";
      const vacationType = query.includes("vacationType=")
        ? query.split("vacationType=")[1]?.split("&")[0] || "all"
        : "all";
      
      const params = {
        limit,
        offset,
        param: query.includes("param=")
          ? query.split("param=")[1]?.split("&")[0] || ""
          : "",
        sort: sortBy,
        order: defaultSort,
        branchId,
        vacationType,
        ...(bossId && { bossId: Number(bossId) }),
      };

      const response = await getAllBalancesPaginated(params);
      dispatch(setBalances(response.data || []));
      dispatch(setTotal(response.total || 0));
    } catch (error) {
      console.error("Error reloading balances:", error);
    }
  };

  const handleEditSuccess = async () => {
    // Buscar el partnerId desde los detalles ya cargados
    let partnerId: number | null = null;
    for (const [pid, details] of partnerDetails.entries()) {
      if (details.periods?.some(p => p.id === editingBalance?.id)) {
        partnerId = pid;
        break;
      }
    }
    
    setShowEditModal(false);
    setEditingBalance(null);
    
    // Recargar los balances agrupados para actualizar los totales
    await reloadBalances();
    
    // Recargar los detalles del partner si está expandido
    if (partnerId && expandedRows.has(partnerId)) {
      await loadPartnerDetails(partnerId);
    }
    
    // Llamar al callback de refresh si existe (desde el componente padre)
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingBalance(null);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeletingBalance(null);
  };

  const renderPeriodDetails = (partnerId: number) => {
    const details = partnerDetails.get(partnerId);
    const isLoading = loadingDetails.has(partnerId);

    if (isLoading) {
      return (
        <tr className="bg-light">
          <td colSpan={11} className="p-0">
            <div className="border-start border-3 border-secondary ms-3 p-3">
              <div className="d-flex align-items-center justify-content-center py-4">
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">Cargando...</span>
                </div>
                <span className="text-muted">Cargando detalles...</span>
              </div>
            </div>
          </td>
        </tr>
      );
    }

    if (!details || !details.periods) {
      return (
        <tr className="bg-light">
          <td colSpan={11} className="p-0">
            <div className="border-start border-3 border-secondary ms-3 p-3">
              <div className="text-center py-4">
                <i className="fas fa-exclamation-triangle text-warning fa-2x mb-2"></i>
                <p className="text-muted mb-0">
                  No se pudieron cargar los detalles
                </p>
              </div>
            </div>
          </td>
        </tr>
      );
    }

    return (
      <tr className="bg-light">
        <td colSpan={11} className="p-0">
          <div className="border-start border-3 border-secondary ms-3 p-3">
            <h6 className="text-secondary mb-3">
              <i className="fas fa-calendar-alt me-2"></i>
              Detalles por Período
            </h6>
            <div className="table-responsive">
              <table className="table table-sm table-borderless mb-0">
                <thead>
                  <tr className="border-bottom">
                    <th className="text-muted small">Período</th>
                    <th className="text-muted small text-center">
                      Días Totales
                    </th>
                    <th className="text-muted small text-center">
                      Días Usados
                    </th>
                    <th className="text-muted small text-center">
                      Días Pendientes
                    </th>
                    <th className="text-muted small">Notas</th>
                    <th className="text-muted small">Fecha</th>
                    <th className="text-muted small text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {details.periods.map((period, index) => (
                    <tr key={index} className="border-bottom-light">
                      <td>
                        <span className="badge bg-primary fs-7 px-2 py-1">
                          {formatPeriod(period.holidayPeriod)}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-secondary fs-7 px-2 py-1">
                          {period.totalDays} días
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-success fs-7 px-2 py-1">
                          {period.daysTaken} días
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="badge bg-warning text-dark fs-7 px-2 py-1">
                          {period.daysPending} días
                        </span>
                      </td>
                      <td>
                        {period.notes ? (
                          <Tooltip title={period.notes} arrow>
                            <span
                              className="text-muted small cursor-pointer"
                              style={{
                                cursor: "help",
                                textDecoration: "underline",
                                textDecorationStyle: "dotted",
                              }}
                            >
                              {period.notes.length > 25
                                ? `${period.notes.substring(0, 25)}...`
                                : period.notes}
                            </span>
                          </Tooltip>
                        ) : (
                          <span className="text-muted small">Sin notas</span>
                        )}
                      </td>
                      <td className="text-muted small">
                        {new Date(period.createdAt).toLocaleDateString(
                          "es-ES",
                          {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                          }
                        )}
                      </td>
                      <td className="text-center">
                        <div className="d-flex gap-1 justify-content-center">
                          <button
                            className="btn btn-outline-warning btn-sm"
                            onClick={() => handleEdit({
                              ...period,
                              holidayPeriod: period.holidayPeriod
                            })}
                            disabled={loading}
                            title="Editar balance"
                            style={{ 
                              width: "28px", 
                              height: "28px", 
                              padding: "0",
                              fontSize: "0.7rem"
                            }}
                          >
                            <i className="fa fa-edit"></i>
                          </button>
                          {/* Botón de eliminar oculto temporalmente */}
                          {false && (
                            <button
                              className="btn btn-outline-danger btn-sm"
                              onClick={() => handleDelete({
                                ...period,
                                holidayPeriod: period.holidayPeriod
                              })}
                              disabled={loading}
                              title="Eliminar balance"
                              style={{ 
                                width: "28px", 
                                height: "28px", 
                                padding: "0",
                                fontSize: "0.7rem"
                              }}
                            >
                              <i className="fa fa-trash"></i>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  const hasNextPage = (page + 1) * limit < total;
  const showingFrom = groupedBalances.length > 0 ? page * limit + 1 : 0;
  const showingTo = Math.min(page * limit + groupedBalances.length, total);

  return (
    <React.Fragment>
      <div
        className="table-responsive mb-3 rounded-3 border"
        style={{ overflowY: "auto", maxHeight: "560px" }}
      >
        <table className="table table-hover table-panel text-nowrap align-middle mb-0">
          <thead
            style={{
              ...fixedHeadStyle,
              background: "#f8fafc",
              boxShadow: "inset 0 -2px 0 #e2e8f0",
            } as React.CSSProperties}
          >
            <tr>
              <th style={{ ...fixedStyle } as React.CSSProperties}>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  onClick={() => setSort("partnerName")}
                >
                  Colaborador {sortBy === "partnerName" && getIconSort()}
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  style={{ cursor: "default" }}
                >
                  Puesto
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  style={{ cursor: "default" }}
                >
                  Fecha de ingreso
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  style={{ cursor: "default" }}
                >
                  Antigüedad
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  onClick={() => setSort("totalDays")}
                >
                  Días Totales {sortBy === "totalDays" && getIconSort()}
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  onClick={() => setSort("totalDaysTaken")}
                >
                  Días Usados {sortBy === "totalDaysTaken" && getIconSort()}
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  onClick={() => setSort("totalDaysPending")}
                >
                  Días Pendientes{" "}
                  {sortBy === "totalDaysPending" && getIconSort()}
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  onClick={() => setSort("totalDaysRemaining")}
                >
                  Días Restantes{" "}
                  {sortBy === "totalDaysRemaining" && getIconSort()}
                </button>
              </th>
              <th>
                <button
                  className="btn btn-link p-0 text-decoration-none text-dark fw-bold"
                  onClick={() => setSort("periodsCount")}
                >
                  Períodos {sortBy === "periodsCount" && getIconSort()}
                </button>
              </th>
              <th>Última Actualización</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {groupedBalances?.length > 0 ? (
              groupedBalances.map((groupedBalance) => (
                <React.Fragment key={groupedBalance.partnerId}>
                  <tr className="border-bottom">
                    <td style={{ ...fixedStyle } as React.CSSProperties}>
                      <div className="d-flex align-items-center">
                        <div className="me-2">
                          <i className="fas fa-user-circle text-secondary"></i>
                        </div>
                        <div>
                          <div className="fw-semibold text-dark">
                            {groupedBalance.partnerName}
                          </div>
                          <small className="text-muted">
                            ID: {groupedBalance.partnerId}
                          </small>
                        </div>
                      </div>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-primary fs-7 px-2 py-1">
                        {groupedBalance.position || "N/A"}
                      </span>
                    </td>
                    <td className="text-center">
                      {groupedBalance.recruitmentDate ? (
                        <span
                          className="badge rounded-pill px-3 py-2 fw-normal"
                          style={{ background: "#f1f5f9", color: "#334155" }}
                        >
                          {moment(groupedBalance.recruitmentDate).format("DD/MM/YYYY")}
                        </span>
                      ) : (
                        <span className="text-muted small">N/A</span>
                      )}
                    </td>
                    <td className="text-center">
                      {groupedBalance.recruitmentDate ? (
                        <span className="text-muted small fw-medium">
                          {getAntiguedad(groupedBalance.recruitmentDate)}
                        </span>
                      ) : (
                        <span className="text-muted small">N/A</span>
                      )}
                    </td>
                    <td>
                      <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                          background: groupedBalance.totalDays > 0 ? "#64748b" : "#f1f5f9",
                          color: groupedBalance.totalDays > 0 ? "#fff" : "#94a3b8",
                        }}
                      >
                        {groupedBalance.totalDays} días
                      </span>
                    </td>
                    <td>
                      <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                          background: groupedBalance.totalDaysTaken > 0 ? "#0d9488" : "#f0fdfa",
                          color: groupedBalance.totalDaysTaken > 0 ? "#fff" : "#94a3b8",
                        }}
                      >
                        {groupedBalance.totalDaysTaken} días
                      </span>
                    </td>
                    <td>
                      <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                          background: groupedBalance.totalDaysPending > 0 ? "#f59e0b" : "#fffbeb",
                          color: groupedBalance.totalDaysPending > 0 ? "#fff" : "#94a3b8",
                        }}
                      >
                        {groupedBalance.totalDaysPending} días
                      </span>
                    </td>
                    <td>
                      <span
                        className="badge rounded-pill px-3 py-2 fw-semibold"
                        style={{
                          background: groupedBalance.totalDaysRemaining > 0 ? "#0284c7" : "#f0f9ff",
                          color: groupedBalance.totalDaysRemaining > 0 ? "#fff" : "#94a3b8",
                        }}
                      >
                        {groupedBalance.totalDaysRemaining} días
                      </span>
                    </td>
                    <td>
                      <span
                        className="badge rounded-pill px-3 py-2 fw-medium"
                        style={{ background: "#ede9fe", color: "#5b21b6" }}
                      >
                        {groupedBalance.periodsCount} períodos
                      </span>
                    </td>
                    <td className="text-muted">
                      {new Date(groupedBalance.lastUpdated).toLocaleDateString(
                        "es-ES",
                        {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        }
                      )}
                    </td>
                    <td>
                      <button
                        className={`btn btn-sm ${
                          expandedRows.has(groupedBalance.partnerId)
                            ? "btn-outline-secondary"
                            : "btn-outline-primary"
                        }`}
                        onClick={() =>
                          toggleRowExpansion(groupedBalance.partnerId)
                        }
                        title={
                          expandedRows.has(groupedBalance.partnerId)
                            ? "Ocultar detalles"
                            : "Ver detalles"
                        }
                        disabled={loadingDetails.has(groupedBalance.partnerId)}
                      >
                        {loadingDetails.has(groupedBalance.partnerId) ? (
                          <>
                            <div
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                            >
                              <span className="visually-hidden">
                                Cargando...
                              </span>
                            </div>
                            Cargando...
                          </>
                        ) : (
                          <>
                            <i
                              className={`fas fa-${
                                expandedRows.has(groupedBalance.partnerId)
                                  ? "chevron-up"
                                  : "chevron-down"
                              } me-1`}
                            ></i>
                            {expandedRows.has(groupedBalance.partnerId)
                              ? "Ocultar"
                              : "Detalles"}
                          </>
                        )}
                      </button>
                    </td>
                  </tr>
                  {expandedRows.has(groupedBalance.partnerId) &&
                    renderPeriodDetails(groupedBalance.partnerId)}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={11}>
                  <div className="py-4">
                    <div className="d-flex">
                      <i className="fas fa-calendar-times fa-5x text-muted mx-auto"></i>
                    </div>
                    <h5 className="text-secondary text-center fs-20px">
                      Aún no existen balances de vacaciones
                    </h5>
                    <h5 className="text-center text-secondary fw-400 fs-15px">
                      No se encontraron registros
                    </h5>
                    <h5 className="text-center text-secondary fw-400">
                      con los criterios de búsqueda
                    </h5>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="d-md-flex align-items-center pt-2 border-top">
        <div className="me-md-auto text-md-left text-center mb-2 mb-md-0">
          <span className="text-muted small">
            Mostrando{" "}
            <strong>{showingFrom}</strong>–<strong>{showingTo}</strong> de{" "}
            <strong>{total}</strong> colaboradores
          </span>
        </div>
        <div className="d-flex align-items-center">
          <span className="me-2 text-muted small">Mostrar:</span>
          <select
            className="form-select form-select-sm me-3"
            style={{ width: "auto", borderRadius: "8px" }}
            value={limit}
            onChange={handleLimitChange}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
        <ul className="pagination pagination-sm mb-0 justify-content-center">
          <li className={`page-item ${page === 0 ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link"
              style={{ borderRadius: "8px 0 0 8px" }}
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 0}
            >
              Anterior
            </button>
          </li>
          <li className="page-item disabled">
            <span className="page-link bg-light text-muted">
              Pág. {page + 1}
            </span>
          </li>
          <li className={`page-item ${!hasNextPage ? "disabled" : ""}`}>
            <button
              type="button"
              className="page-link"
              style={{ borderRadius: "0 8px 8px 0" }}
              onClick={() => handlePageChange(page + 1)}
              disabled={!hasNextPage}
            >
              Siguiente
            </button>
          </li>
        </ul>
      </div>

      {/* Modal de edición */}
      <ModalContainer
        open={showEditModal}
        closeFn={handleCloseEditModal}
        title="Editar Balance de Vacaciones"
        size="lg"
      >
        {editingBalance && (
          <HolidayBalanceForm
            close={handleCloseEditModal}
            onSuccess={handleEditSuccess}
            balanceToEdit={editingBalance}
          />
        )}
      </ModalContainer>

      {/* Modal de confirmación de eliminación */}
      <ModalContainer
        open={showDeleteModal}
        closeFn={handleCloseDeleteModal}
        title="Eliminar Balance de Vacaciones"
        size="sm"
      >
        <div className="p-4">
          <div className="alert alert-warning mb-3" role="alert">
            <i className="fa fa-exclamation-triangle me-2"></i>
            <strong>¿Estás seguro?</strong>
          </div>
          <p className="mb-3">
            Se eliminará permanentemente el balance de vacaciones para el período{" "}
            <strong>
              {deletingBalance?.holidayPeriod?.startYear}-{deletingBalance?.holidayPeriod?.endYear}
            </strong>
            . Esta acción no se puede deshacer.
          </p>
          <div className="d-flex justify-content-end gap-2">
            <button
              className="btn btn-secondary"
              onClick={handleCloseDeleteModal}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              className="btn btn-danger"
              onClick={handleDeleteConfirm}
              disabled={loading}
            >
              {loading ? (
                <>
                  <i className="fa fa-spinner fa-spin me-1"></i>
                  Eliminando...
                </>
              ) : (
                <>
                  <i className="fa fa-trash me-1"></i>
                  Eliminar
                </>
              )}
            </button>
          </div>
        </div>
      </ModalContainer>
    </React.Fragment>
  );
};

export default HolidayBalancesGroupedTable;
